import React, { FC, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@material-ui/icons';
import { useToaster } from '@/components/core/Toaster';
import {
  ATTACH_DATA_EXPORT_SCHEDULE_CHANNEL,
  AttachDataExportScheduleChannelData,
  AttachDataExportScheduleChannelVars,
  CREATE_DATA_EXPORT_SCHEDULE,
  CreateDataExportScheduleData,
  CreateDataExportScheduleVars,
  DataExportDefinition,
  DataExportDeliveryChannel,
  DataExportSchedule,
  DataExportScheduleRangeEndAnchor,
  DETACH_DATA_EXPORT_SCHEDULE_CHANNEL,
  DetachDataExportScheduleChannelData,
  DetachDataExportScheduleChannelVars,
  VOID_DATA_EXPORT_SCHEDULE,
  VoidDataExportScheduleData,
  VoidDataExportScheduleVars,
} from '@/graph/data-export-admin';
import SelectInput from '@/se/components/inputs/SelectInput';
import { BatchHistoryTable, ChannelSelectInput, ExportPicker } from './manual-tab';
import {
  BatchStatusChip,
  SectionShell,
  definitionKey,
  exportLabel,
  formatHour,
  useTimestampFormatter,
  useSharedStyles,
} from './shared';

/**
 * Scheduled tab — read-only listing of `~~export_definition` rows with
 * schedules, an inline per-schedule channel attach/detach UI driven by
 * `~~export_schedule_channel`, a failure banner for last-scheduled-run
 * problems, and the scheduled-trigger history table.
 *
 * `attachedChannelIds` rides on every schedule from the parent
 * `DATA_EXPORT_DEFINITIONS` query — there is no separate per-schedule
 * fetch. Attach/detach mutations call `refetchDefinitions` so the
 * chips re-render off the canonical source.
 */
export const ScheduledTab: FC<{
  organizationId: number;
  definitions: DataExportDefinition[];
  channels: DataExportDeliveryChannel[];
  advancedMode: boolean;
  refetchDefinitions: () => void;
}> = ({ organizationId, definitions, channels, advancedMode, refetchDefinitions }) => {
  const classes = useSharedStyles();
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpHistory = () => setRefreshKey(k => k + 1);
  // Mirrors the Manual tab: the create form lives in a fixed left column
  // and the listings (definitions + run history) flow in the wider right
  // column. Collapses to a single stacked column on small screens.
  return (
    <div className={classes.manualSplit}>
      <div className={classes.manualFormCol}>
        <ScheduleTriggerPanel
          organizationId={organizationId}
          definitions={definitions}
          channels={channels}
          onCreated={refetchDefinitions}
          vertical
        />
      </div>
      <div className={classes.manualTableCol}>
        <div className={classes.tabContent}>
          <ScheduledFailureBanner definitions={definitions} />
          <DefinitionsTable
            organizationId={organizationId}
            definitions={definitions}
            channels={channels}
            refetchDefinitions={refetchDefinitions}
          />
          <BatchHistoryTable
            organizationId={organizationId}
            // Both 'scheduled' (new writers post the unified-trigger redesign)
            // and 'schedule' (legacy rows under the prior model). Without
            // both, this tab would silently miss new rows once the coordinator
            // switches what it writes.
            triggeredBy={['scheduled', 'schedule']}
            channels={channels}
            advancedMode={advancedMode}
            refreshKey={refreshKey}
            title="Recent scheduled runs"
            emptyMessage="No scheduled runs yet."
            onActionSettled={bumpHistory}
          />
        </div>
      </div>
    </div>
  );
};

/* ---------- create-schedule panel (always-visible form) ---------- */

const ALL_HOURS: number[] = Array.from({ length: 24 }, (_, i) => i);

const RANGE_END_ANCHOR_OPTIONS: Record<DataExportScheduleRangeEndAnchor, string> = {
  yesterday: 'Yesterday (complete days)',
  today: 'Today (includes partial day)',
};

/**
 * Operator-readable coverage of one schedule: what each firing exports.
 * Coverage comes from the schedule (lookback + anchor) — the
 * definition's window_days is production chunking and is deliberately
 * NOT shown here.
 */
export const formatCoverage = (schedule: Pick<DataExportSchedule, 'lookbackDays' | 'rangeEndAnchor'>): string => {
  if (schedule.lookbackDays === 1) {
    return schedule.rangeEndAnchor === 'today' ? 'Today (partial)' : 'Yesterday';
  }
  return `Last ${schedule.lookbackDays} days, ending ${schedule.rangeEndAnchor}`;
};

/**
 * Always-visible "add a scheduled firing" form — the inline equivalent of
 * the Manual tab's trigger panel, reusing the same `SelectInput`-based
 * export + channel pickers. Replaces the former modal dialog so the create
 * affordance is permanently on screen in the left column.
 *
 * Channel selection is REQUIRED (unlike the Manual trigger): a scheduled
 * tick fires unattended, so a channel-less schedule would silently produce
 * nothing on every firing. Hours already taken by the chosen definition are
 * dropped from the picker so the duplicate-firing-hour unique index never
 * gets a chance to reject the create server-side.
 */
const ScheduleTriggerPanel: FC<{
  organizationId: number;
  definitions: DataExportDefinition[];
  channels: DataExportDeliveryChannel[];
  onCreated: () => void;
  vertical?: boolean;
}> = ({ organizationId, definitions, channels, onCreated, vertical = false }) => {
  const classes = useSharedStyles();
  const toaster = useToaster();
  const fullWhenVertical = vertical ? classes.fieldFull : '';
  const [definitionKeyValue, setDefinitionKeyValue] = useState('');
  const [firingHour, setFiringHour] = useState<number | ''>('');
  // Coverage: how many days each firing exports and where the range
  // ends. Defaults: 1 day, ending yesterday (complete days only) — the
  // conventional daily-feed shape. Kept as string for the TextField;
  // parsed at submit/validation time.
  const [lookbackDaysInput, setLookbackDaysInput] = useState('1');
  const [rangeEndAnchor, setRangeEndAnchor] = useState<DataExportScheduleRangeEndAnchor>('yesterday');
  const [channelIds, setChannelIds] = useState<number[]>([]);

  const selectedDef = useMemo(
    () => definitions.find(d => definitionKey(d) === definitionKeyValue),
    [definitions, definitionKeyValue]
  );

  // Reset the firing hour and channels when the definition changes so a
  // previously-valid combo doesn't carry over to a different definition
  // (whose used-hours set differs).
  useEffect(() => {
    setFiringHour('');
    setChannelIds([]);
  }, [definitionKeyValue]);

  // Hours already used by the chosen definition's active schedules are
  // omitted from the options entirely (cleaner than disabled rows in the
  // react-select menu).
  const hourOptions = useMemo(() => {
    const used = new Set(selectedDef?.schedules.map(s => s.firingHour) ?? []);
    const map: Record<string, string> = {};
    for (const h of ALL_HOURS) if (!used.has(h)) map[h] = formatHour(h);
    return map;
  }, [selectedDef]);

  const [createSchedule, { loading }] = useMutation<CreateDataExportScheduleData, CreateDataExportScheduleVars>(
    CREATE_DATA_EXPORT_SCHEDULE
  );

  // Validate the RAW input as a whole number — parseInt('1.5') quietly
  // truncates to 1, which would create a schedule with different
  // coverage than the operator typed. The onChange gate below already
  // blocks non-digit entry; this is the belt to that suspender (paste,
  // browser quirks).
  const lookbackValid = /^[0-9]+$/.test(lookbackDaysInput) && Number.parseInt(lookbackDaysInput, 10) > 0;
  const lookbackDays = lookbackValid ? Number.parseInt(lookbackDaysInput, 10) : 0;

  const canSubmit =
    !!selectedDef && typeof firingHour === 'number' && lookbackValid && channelIds.length > 0 && !loading;

  const onSubmit = async () => {
    if (!selectedDef || typeof firingHour !== 'number' || !lookbackValid || channelIds.length === 0) return;
    try {
      const { data, errors } = await createSchedule({
        variables: {
          organizationId,
          definitionId: selectedDef.id,
          firingHour,
          lookbackDays,
          rangeEndAnchor,
          channelIds,
        },
      });
      if (errors && errors.length > 0) {
        toaster.error(`Could not add schedule: ${errors[0]!.message}`);
        return;
      }
      if (!data?.createDataExportSchedule) {
        toaster.error('Could not add schedule: no schedule returned');
        return;
      }
      toaster.success(
        `Scheduled ${exportLabel(selectedDef.name)} at ${formatHour(firingHour)} covering ${formatCoverage({ lookbackDays, rangeEndAnchor }).toLowerCase()}.`
      );
      setDefinitionKeyValue('');
      setFiringHour('');
      setLookbackDaysInput('1');
      setRangeEndAnchor('yesterday');
      setChannelIds([]);
      onCreated();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown error';
      toaster.error(`Could not add schedule: ${message}`);
    }
  };

  return (
    <SectionShell title="Add a scheduled export">
      <div className={vertical ? classes.controlsColumn : classes.controlsRow}>
        <ExportPicker
          definitions={definitions}
          value={definitionKeyValue}
          onChange={setDefinitionKeyValue}
          disabled={loading}
          className={fullWhenVertical}
        />
        <div className={`${classes.fieldDateRange} ${fullWhenVertical}`}>
          <SelectInput
            name="firing-hour"
            options={hourOptions}
            value={firingHour === '' ? '' : String(firingHour)}
            onChange={(h?: string) => setFiringHour(h ? Number(h) : '')}
            placeholder={selectedDef ? 'Firing hour (org-local)' : 'Pick an export first'}
            isDisabled={!selectedDef || loading}
            isClearable
            sortOptions
            menuWidthFollowContent={false}
          />
        </div>
        <TextField
          label="Coverage (days)"
          type="number"
          size="small"
          variant="outlined"
          inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
          value={lookbackDaysInput}
          // Whole days only: refuse to commit anything but digits to
          // state (covers paste), and swallow the keys a number input
          // would otherwise accept ('.', 'e', '-', '+').
          onChange={e => {
            const next = e.target.value;
            if (next === '' || /^[0-9]+$/.test(next)) setLookbackDaysInput(next);
          }}
          onKeyDown={e => {
            if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
          }}
          error={lookbackDaysInput !== '' && !lookbackValid}
          helperText={lookbackDaysInput !== '' && !lookbackValid ? 'Must be a positive whole number' : undefined}
          disabled={loading}
          className={fullWhenVertical}
        />
        <div className={`${classes.fieldDateRange} ${fullWhenVertical}`}>
          <SelectInput
            name="range-end-anchor"
            options={RANGE_END_ANCHOR_OPTIONS}
            value={rangeEndAnchor}
            onChange={(a?: string) => setRangeEndAnchor(a === 'today' ? 'today' : 'yesterday')}
            placeholder="Range ends"
            isDisabled={loading}
            menuWidthFollowContent={false}
          />
        </div>
        <ChannelSelectInput
          channels={channels}
          value={channelIds}
          onChange={setChannelIds}
          loading={loading}
          disabled={!selectedDef}
          placeholder="Channels (required)"
          className={`${classes.fieldChannelMulti} ${fullWhenVertical}`}
        />
      </div>
      <div className={classes.actionRow}>
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          Add schedule
        </Button>
      </div>
    </SectionShell>
  );
};

/* ---------- failure banner ---------- */

/**
 * A definition's last scheduled batch needs operator attention when
 * production/merge failed (presented status) OR when the batch
 * produced fine but at least one channel push failed — both are
 * recoverable from the history table (Retry / Redeliver).
 */
export const lastScheduledBatchNeedsAttention = (d: DataExportDefinition): boolean =>
  !!d.lastScheduledBatch && (d.lastScheduledBatch.status === 'failed' || d.lastScheduledBatch.hasFailedDeliveries);

const ScheduledFailureBanner: FC<{ definitions: DataExportDefinition[] }> = ({ definitions }) => {
  const classes = useSharedStyles();
  // Batch-level on purpose: scheduled firings produce multiple window
  // runs, and the newest run can be success while a sibling window
  // failed — the batch status aggregates all of them, and
  // hasFailedDeliveries covers the delivery axis the batch status
  // deliberately excludes.
  const needsAttention = useMemo(() => definitions.filter(lastScheduledBatchNeedsAttention), [definitions]);

  if (needsAttention.length === 0) return null;
  return (
    <Paper className={classes.failureBanner} elevation={1}>
      <ErrorIcon className={classes.failureBannerIcon} />
      <div>
        <Typography variant="subtitle1" className={classes.failureBannerTitle}>
          {needsAttention.length === 1
            ? '1 scheduled export had its last batch fail or not deliver'
            : `${needsAttention.length} scheduled exports had their last batch fail or not deliver`}
        </Typography>
        <Typography variant="body2">{needsAttention.map(d => exportLabel(d.name)).join(', ')}</Typography>
      </div>
    </Paper>
  );
};

/* ---------- definitions list ---------- */

const DefinitionsTable: FC<{
  organizationId: number;
  definitions: DataExportDefinition[];
  channels: DataExportDeliveryChannel[];
  refetchDefinitions: () => void;
}> = ({ organizationId, definitions, channels, refetchDefinitions }) => {
  const classes = useSharedStyles();
  const scheduled = useMemo(() => definitions.filter(d => d.schedules.length > 0), [definitions]);
  return (
    <SectionShell title="Scheduled exports">
      {scheduled.length === 0 ? (
        <div className={classes.tableEmpty}>
          <Typography variant="body2">
            {definitions.length === 0
              ? 'No definitions configured for this organization.'
              : 'No scheduled exports yet — use the “Add a schedule” form on the left.'}
          </Typography>
        </div>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell}>Export</TableCell>
                <TableCell className={classes.headCell}>Coverage</TableCell>
                <TableCell className={classes.headCell}>Firing hour</TableCell>
                <TableCell className={classes.headCell}>Format</TableCell>
                <TableCell className={classes.headCell}>Attached channels</TableCell>
                <TableCell className={classes.headCell}>Last scheduled batch</TableCell>
                <TableCell align="right" className={classes.headCell}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduled.flatMap(def =>
                def.schedules.map(schedule => (
                  <ScheduleRow
                    key={`${definitionKey(def)}:${schedule.id}`}
                    organizationId={organizationId}
                    definition={def}
                    schedule={schedule}
                    channels={channels}
                    refetchDefinitions={refetchDefinitions}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionShell>
  );
};

const ScheduleRow: FC<{
  organizationId: number;
  definition: DataExportDefinition;
  schedule: DataExportSchedule;
  channels: DataExportDeliveryChannel[];
  refetchDefinitions: () => void;
}> = ({ organizationId, definition, schedule, channels, refetchDefinitions }) => {
  const classes = useSharedStyles();
  const toaster = useToaster();
  const fmt = useTimestampFormatter();
  const last = definition.lastScheduledBatch;
  // Mirror the banner's logic: batch-level production failure OR a
  // failed delivery on the most recent scheduled batch. Recovery for
  // both lives in the history table (Retry / Redeliver).
  const lastNeedsAttention = lastScheduledBatchNeedsAttention(definition);

  const [voidSchedule, { loading: voiding }] = useMutation<VoidDataExportScheduleData, VoidDataExportScheduleVars>(
    VOID_DATA_EXPORT_SCHEDULE
  );

  const hourLabel = formatHour(schedule.firingHour);
  const onVoid = async () => {
    // Native confirm is fine here — the action is reversible at the data
    // level (we can re-add the same schedule from the dialog after a
    // mistake) so we don't need a richer Material confirm dialog.
    const ok = window.confirm(`Stop scheduling ${exportLabel(definition.name)} at ${hourLabel}?`);
    if (!ok) return;
    try {
      const { data, errors } = await voidSchedule({
        variables: { organizationId, scheduleId: schedule.id },
      });
      if (errors && errors.length > 0) {
        toaster.error(`Could not remove schedule: ${errors[0]!.message}`);
        return;
      }
      if (data?.voidDataExportSchedule) {
        toaster.success(`Removed ${hourLabel} firing for ${exportLabel(definition.name)}.`);
      } else {
        // Already voided server-side (race vs. another operator). Treat as
        // a soft no-op — refetch will sync the visible state.
        toaster.info('Schedule was already removed.');
      }
      refetchDefinitions();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown error';
      toaster.error(`Could not remove schedule: ${message}`);
    }
  };

  return (
    <TableRow hover>
      <TableCell className={classes.cellNowrap}>{exportLabel(definition.name)}</TableCell>
      {/* Coverage is the SCHEDULE's lookback+anchor; the definition's
          window_days is production chunking and is intentionally not
          surfaced here. */}
      <TableCell className={classes.cellNowrap}>{formatCoverage(schedule)}</TableCell>
      <TableCell className={classes.cellNowrap}>{hourLabel}</TableCell>
      <TableCell>{definition.outputFormat ?? '—'}</TableCell>
      <TableCell>
        <ScheduleChannelEditor
          organizationId={organizationId}
          scheduleId={schedule.id}
          attachedChannelIds={schedule.attachedChannelIds}
          channels={channels}
          onChanged={refetchDefinitions}
        />
      </TableCell>
      <TableCell>
        {last ? (
          <Tooltip
            arrow
            placement="top"
            title={
              <>
                <div>{fmt(last.createdAt)}</div>
                <div>
                  {last.rangeStart} → {last.rangeEnd}
                </div>
                {last.errorMessage && <div>{last.errorMessage}</div>}
                {last.hasFailedDeliveries && (
                  <div>At least one delivery failed — redeliver from the history table.</div>
                )}
              </>
            }
          >
            <span>
              <BatchStatusChip status={last.status} />
              {lastNeedsAttention && (
                <WarningIcon fontSize="small" color="error" className={classes.inlineWarningIcon} />
              )}
            </span>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="textSecondary">
            never
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Remove this firing hour">
          <span>
            <IconButton size="small" onClick={onVoid} disabled={voiding} aria-label="remove-schedule">
              {voiding ? <CircularProgress size={14} /> : <DeleteIcon fontSize="inherit" />}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

/* ---------- attach/detach editor ---------- */

const ScheduleChannelEditor: FC<{
  organizationId: number;
  scheduleId: number;
  attachedChannelIds: number[];
  channels: DataExportDeliveryChannel[];
  onChanged: () => void;
}> = ({ organizationId, scheduleId, attachedChannelIds, channels, onChanged }) => {
  const classes = useSharedStyles();
  const toaster = useToaster();

  const attachedSet = useMemo(() => new Set(attachedChannelIds), [attachedChannelIds]);
  const attached = useMemo(() => channels.filter(c => attachedSet.has(c.id)), [channels, attachedSet]);
  const detachable = useMemo(() => channels.filter(c => !attachedSet.has(c.id)), [channels, attachedSet]);

  const [attach, { loading: attaching }] = useMutation<
    AttachDataExportScheduleChannelData,
    AttachDataExportScheduleChannelVars
  >(ATTACH_DATA_EXPORT_SCHEDULE_CHANNEL);
  const [detach, { loading: detaching }] = useMutation<
    DetachDataExportScheduleChannelData,
    DetachDataExportScheduleChannelVars
  >(DETACH_DATA_EXPORT_SCHEDULE_CHANNEL);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState<number | ''>('');

  const onAttach = async () => {
    if (typeof pickerValue !== 'number') return;
    try {
      const { data: result } = await attach({
        variables: { organizationId, scheduleId, channelId: pickerValue },
      });
      if (result?.attachDataExportScheduleChannel) {
        toaster.success('Channel attached');
      } else {
        toaster.error('Channel was already attached');
      }
      setPickerValue('');
      setPickerOpen(false);
      onChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toaster.error(`Attach failed: ${message}`);
    }
  };

  const onDetach = async (channelId: number) => {
    try {
      const { data: result } = await detach({
        variables: { organizationId, scheduleId, channelId },
      });
      if (result?.detachDataExportScheduleChannel) {
        toaster.success('Channel detached');
      } else {
        toaster.error('Channel was not attached');
      }
      onChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toaster.error(`Detach failed: ${message}`);
    }
  };

  return (
    <Box display="flex" flexWrap="wrap" alignItems="center">
      {attached.length === 0 ? (
        <Typography variant="caption" color="textSecondary" className={classes.noChannelsCaption}>
          No channels
        </Typography>
      ) : (
        attached.map(ch => (
          <Chip
            key={ch.id}
            size="small"
            className={classes.channelChip}
            // Operator-facing name only — see ChannelMultiSelect in
            // shared.tsx for the rationale (type is a
            // worker-side transport, not a customer-facing label).
            label={ch.name}
            onDelete={() => onDetach(ch.id)}
            deleteIcon={
              detaching ? (
                <CircularProgress size={12} />
              ) : (
                <CloseIcon fontSize="small" aria-label={`detach-${ch.name}`} />
              )
            }
          />
        ))
      )}
      {pickerOpen ? (
        <Box display="flex" alignItems="center">
          <FormControl size="small" variant="outlined" className={classes.attachPickerField}>
            <InputLabel shrink>Add channel</InputLabel>
            <Select
              value={pickerValue}
              onChange={e => setPickerValue(e.target.value as number)}
              label="Add channel"
              displayEmpty
            >
              <MenuItem value="" disabled>
                {detachable.length === 0 ? 'All channels attached' : 'Select…'}
              </MenuItem>
              {detachable.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={onAttach}
            disabled={typeof pickerValue !== 'number' || attaching}
            aria-label="confirm-attach"
          >
            {attaching ? <CircularProgress size={14} /> : <AddIcon fontSize="inherit" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setPickerOpen(false);
              setPickerValue('');
            }}
            disabled={attaching}
            aria-label="cancel-attach"
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Box>
      ) : (
        <Tooltip title={detachable.length === 0 ? 'All channels attached' : 'Attach a channel'}>
          <span>
            <IconButton
              size="small"
              onClick={() => setPickerOpen(true)}
              disabled={detachable.length === 0}
              aria-label="open-attach-picker"
            >
              <AddIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
